import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PORTAL_NAVIGATION_COUNTS_INVALIDATED_EVENT, apiAcknowledgeStaffNotification, apiListStaffActivityNotifications, type StaffActivityNotification } from "@/lib/api";

export interface NotificationItem {
  id: string;
  activity_log_id?: string;
  status?: "unread" | "read";
  created_at?: string;
  category: "intake_documents" | "appointments" | "billing" | "claims" | "supervision" | "forms_surveys" | "tasks" | "system_practice";
  title: string;
  description: string;
  route: string;
  urgent?: boolean;
  persistent?: boolean;
  items?: NotificationItem[];
}
export interface NotificationCategory { key: NotificationItem["category"]; label: string; icon: string; items: NotificationItem[]; count: number; }
const categoryLabels = {appointments:["Appointments","ri-calendar-line"],tasks:["Tasks","ri-task-line"],supervision:["Supervision","ri-shield-check-line"],forms_surveys:["Documents","ri-file-list-3-line"],system_practice:["Practice","ri-settings-3-line"]} as const;

// Activity Log deliveries and staff acknowledgements own notification/read state.
// Operational requirement and My Work counts remain in their existing workspaces.
export function useNotificationData() {
  const [notifications,setNotifications]=useState<StaffActivityNotification[]>([]);
  const [totalCount,setTotalCount]=useState(0);
  const [hasMore,setHasMore]=useState(false);
  const [loadingMore,setLoadingMore]=useState(false);
  const [loadState,setLoadState]=useState({loading:true,available:false,failed:false});
  const [readError,setReadError]=useState("");
  const generationRef=useRef(0);
  const moreInFlight=useRef(false);
  useEffect(()=>{
    let active=true,inFlight=false,invalidated=false;
    let timer:ReturnType<typeof setTimeout>|undefined;
    let controller:AbortController|undefined;
    const schedule=(mutation=false)=>{
      if(!active)return;if(mutation)invalidated=true;
      if(document.visibilityState==='hidden'||inFlight||timer)return;
      timer=setTimeout(()=>{timer=undefined;void refresh();},100);
    };
    const refresh=async()=>{
      if(!active||document.visibilityState==='hidden')return;
      inFlight=true;invalidated=false;controller=new AbortController();const generation=++generationRef.current;
      setLoadState(state=>({...state,loading:true}));
      try{
        const response=await apiListStaffActivityNotifications({signal:controller.signal});
        if(!active||generation!==generationRef.current)return;
        if(response.data){setNotifications(response.data.notifications);setTotalCount(response.data.unread_count);setHasMore(response.data.has_more);setLoadState({loading:false,available:true,failed:false});}
        else setLoadState(state=>({...state,loading:false,failed:true}));
      }finally{inFlight=false;if(active&&invalidated)schedule();}
    };
    schedule();const interval=window.setInterval(()=>schedule(),120_000);
    const focus=()=>schedule(),invalidate=()=>schedule(true),visibility=()=>{if(document.visibilityState==='visible')schedule();};
    window.addEventListener('focus',focus);window.addEventListener(PORTAL_NAVIGATION_COUNTS_INVALIDATED_EVENT,invalidate);document.addEventListener('visibilitychange',visibility);
    return()=>{active=false;controller?.abort();if(timer)clearTimeout(timer);window.clearInterval(interval);window.removeEventListener('focus',focus);window.removeEventListener(PORTAL_NAVIGATION_COUNTS_INVALIDATED_EVENT,invalidate);document.removeEventListener('visibilitychange',visibility);};
  },[]);
  const markRead=useCallback(async(item:NotificationItem)=>{
    if(item.status==='read')return true;
    if(!item.activity_log_id)return false;
    const result=await apiAcknowledgeStaffNotification(item.activity_log_id);
    if(!result.data?.acknowledged){setReadError("The notification could not be marked read. Please try again.");return false;}
    setReadError("");
    // Re-read persisted authority; acknowledging never mutates an associated task.
    const refreshed=await apiListStaffActivityNotifications();
    if(refreshed.data){++generationRef.current;setNotifications(refreshed.data.notifications);setTotalCount(refreshed.data.unread_count);setHasMore(refreshed.data.has_more);setLoadState({loading:false,available:true,failed:false});}
    else {
      // The successful acknowledgment is persisted authority even if the next
      // read fails. Reflect that acknowledgment and disclose the stale count.
      ++generationRef.current;setNotifications(current=>current.map(notification=>notification.id===item.id?{...notification,status:'read'}:notification));setTotalCount(count=>Math.max(0,count-(item.status==='unread'?1:0)));setLoadState({loading:false,available:false,failed:true});setReadError('Read status saved. The notification list could not be refreshed; retry when the connection recovers.');
    }
    return true;
  },[]);
  const loadMore=useCallback(async()=>{
    if(moreInFlight.current||!hasMore||loadState.loading)return;
    moreInFlight.current=true;setLoadingMore(true);const generation=generationRef.current;
    try{
      const response=await apiListStaffActivityNotifications({offset:notifications.length});
      if(generation!==generationRef.current)return;
      if(response.data){const data=response.data;setNotifications(current=>[...new Map([...current,...data.notifications].map(item=>[item.id,item])).values()]);setTotalCount(data.unread_count);setHasMore(data.has_more);}
      else setReadError("More notifications could not be loaded. Please try again.");
    }finally{moreInFlight.current=false;setLoadingMore(false);}
  },[hasMore,loadState.loading,notifications.length]);
  const categories=useMemo(()=>Object.entries(categoryLabels).flatMap(([key,[label,icon]])=>{
    const items=notifications.filter(item=>item.category===key);
    return items.length?[{key:key as NotificationItem['category'],label,icon,items,count:items.filter(item=>item.status==='unread').length}]:[];
  }),[notifications]);
  return {categories,totalCount,available:loadState.available&&!loadState.failed,loading:loadState.loading,failedFeeds:loadState.failed?["notifications"]:[],markRead,readError,hasMore,loadMore,loadingMore};
}
